/** Info tab — every request attribute as copyable rows, the booking summary
 *  the webhook carried, the bookings/orders it touched (console links), and
 *  the raw headers / extracted data JSON. */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Box,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { ApiRequestDetail } from '@/api/apiRequests';
import { JsonEditorWidget } from '@/components/JsonEditor/JsonEditorWidget';
import { SectionCard } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { TsValue } from '@/features/bgtasks/taskFormat';

import { apiRequestStoreLabel, apiRequestTypeLabel, formatMs } from '../apiRequestFormat';
import {
  apiRequestIdActions,
  bookingDateActions,
  contactActions,
  errorSeriesActions,
  externalBookingIdActions,
  guestEmailActions,
  integrationActions,
  ipActions,
  linkedBookingActions,
  linkedOrderActions,
  orderRefActions,
  requestIdActions,
  storeActions,
  tenantActions,
  typeActions,
  uuidActions,
  type ApiRequestCtx,
} from '../apiRequestLinks';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

function CopyRow({
  label,
  value,
  monospace = true,
  actions,
}: {
  label: string;
  value: string | null | undefined;
  monospace?: boolean;
  /** Where this value leads. Empty values never get a popover. */
  actions?: ValueAction[];
}) {
  const snackbar = useSnackbar();
  const text = value ? (
    <Typography variant="body2" component="span" sx={{ ...(monospace ? mono : {}), wordBreak: 'break-all' }}>
      {value}
    </Typography>
  ) : null;
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 32 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>
        {label}
      </Typography>
      {value ? (
        <>
          {actions?.length ? <ValueActions actions={actions}>{text}</ValueActions> : text}
          <Tooltip title={`Copy ${label.toLowerCase()}`}>
            <IconButton
              size="small"
              aria-label={`Copy ${label.toLowerCase()}`}
              onClick={() => {
                void navigator.clipboard.writeText(value);
                snackbar.show(`${label} copied.`, 'success');
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Typography variant="body2" color="text.disabled">—</Typography>
      )}
    </Stack>
  );
}

function PlainRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 32 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" component="span">{children}</Typography>
    </Stack>
  );
}

export function InfoTab({ detail }: { detail: ApiRequestDetail }) {
  const d = detail;
  const ctx: ApiRequestCtx = { tenantId: d.tenant?.id ?? null, createdTs: d.created_ts };
  const hasBookingSummary =
    d.booking_contact || d.booking_status || d.booking_product || d.booking_datetime || d.booking_tickets;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard title="Request">
        <CopyRow label="ID" value={String(d.id)} actions={apiRequestIdActions(d.id, ctx)} />
        <CopyRow label="UUID" value={d.uuid} {...(d.uuid && { actions: uuidActions(d.uuid, ctx) })} />
        <CopyRow
          label="Request ID"
          value={d.request_id}
          {...(d.request_id && { actions: requestIdActions(d.request_id, ctx) })}
        />
        {/* The VENDOR's booking id: `id=` on /bookings, `booking=` elsewhere. */}
        <CopyRow
          label="Booking ID"
          value={d.booking_id}
          {...(d.booking_id && { actions: externalBookingIdActions(d.booking_id, ctx) })}
        />
        <PlainRow label="Tenant">
          {d.tenant ? (
            <ValueActions actions={tenantActions(d.tenant, ctx)}>
              <Link component={RouterLink} to={`/tour-operators/${d.tenant.id}`} variant="body2">
                {d.tenant.name}
              </Link>
            </ValueActions>
          ) : (
            <Typography variant="body2" color="text.disabled" component="span">—</Typography>
          )}
        </PlainRow>
        <CopyRow label="Name" value={d.name} monospace={false} />
        <CopyRow
          label="Integration"
          value={d.integration}
          {...(d.integration && { actions: integrationActions(d.integration, ctx) })}
        />
        <CopyRow
          label="Type"
          value={d.type != null ? apiRequestTypeLabel(d.type) : null}
          monospace={false}
          {...(d.type != null && { actions: typeActions(apiRequestTypeLabel(d.type), ctx) })}
        />
        <CopyRow label="Method" value={d.method} />
        <CopyRow label="URL" value={d.url} />
        <CopyRow label="Content type" value={d.content_type} />
        <CopyRow label="Django view" value={d.view} />
        <CopyRow label="Origin IP" value={d.ip} {...(d.ip && { actions: ipActions(d.ip) })} />
        <PlainRow label="Received">
          <TsValue value={d.created_ts} />
        </PlainRow>
        <PlainRow label="Duration">{formatMs(d.duration_ms)}</PlainRow>
        <PlainRow label="Store policy">
          {d.store != null ? (
            <ValueActions actions={storeActions(apiRequestStoreLabel(d.store), ctx)}>
              {apiRequestStoreLabel(d.store)}
            </ValueActions>
          ) : (
            apiRequestStoreLabel(d.store)
          )}
          {d.store === 1 && d.expires_ts ? (
            <>
              {' — expires '}
              <TsValue value={d.expires_ts} />
            </>
          ) : null}
        </PlainRow>
        <CopyRow
          label="Error series"
          value={d.error_series_id}
          {...(d.error_series_id && { actions: errorSeriesActions(d.error_series_id, ctx) })}
        />
        <CopyRow label="Error" value={d.error} monospace={false} />
      </SectionCard>

      {hasBookingSummary && (
        <SectionCard title="Booking summary">
          <CopyRow
            label="Contact"
            value={d.booking_contact}
            monospace={false}
            {...(d.booking_contact && { actions: contactActions(d.booking_contact, ctx) })}
          />
          <CopyRow label="Status" value={d.booking_status} monospace={false} />
          <CopyRow label="Product" value={d.booking_product} monospace={false} />
          <CopyRow label="Date & time" value={d.booking_datetime} monospace={false} />
          <CopyRow label="Tickets" value={d.booking_tickets} monospace={false} />
        </SectionCard>
      )}

      {d.bookings.length > 0 && (
        <SectionCard title={`Affected bookings (${d.bookings.length})`}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Booking</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Tour</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.bookings.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>
                    <ValueActions actions={linkedBookingActions(b, ctx)}>
                      <Link component={RouterLink} to={`/bookings/${b.id}`} sx={mono}>
                        {b.id}
                      </Link>
                    </ValueActions>
                  </TableCell>
                  <TableCell>
                    {b.email ? (
                      <ValueActions actions={guestEmailActions(b.email, ctx)}>{b.email}</ValueActions>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  {/* Tour NAME only — the row carries no tour_id (Phase 8). */}
                  <TableCell>{b.tour || '—'}</TableCell>
                  <TableCell>
                    {b.date ? (
                      <ValueActions actions={bookingDateActions(b.date, ctx)}>{b.date}</ValueActions>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {d.orders.length > 0 && (
        <SectionCard title={`Orders (${d.orders.length})`}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Reference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.orders.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell>
                    <ValueActions actions={linkedOrderActions(o)}>
                      <Link component={RouterLink} to={`/orders/${o.id}`} sx={mono}>
                        {o.id}
                      </Link>
                    </ValueActions>
                  </TableCell>
                  <TableCell sx={mono}>
                    {o.order_ref ? (
                      <ValueActions actions={orderRefActions(o.order_ref, ctx)}>{o.order_ref}</ValueActions>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {(d.headers != null || d.data != null) && (
        <SectionCard title="Headers & data">
          {d.headers != null && (
            <Box sx={{ mb: d.data != null ? 2 : 0 }}>
              <Typography variant="caption" color="text.secondary">headers</Typography>
              <JsonEditorWidget initialValue={d.headers} readOnly mode="view" />
            </Box>
          )}
          {d.data != null && (
            <Box>
              <Typography variant="caption" color="text.secondary">data</Typography>
              <JsonEditorWidget initialValue={d.data} readOnly mode="view" />
            </Box>
          )}
        </SectionCard>
      )}
    </Box>
  );
}
